import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { AlertPosition, AlertType } from '../../../shared/enum/EnumRepository';
import { useAuth } from '../../../shared/hooks/useApi/useAuth';
import { useAuthTokenStore } from '../../../shared/store/authStore';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import {
  EXHIBITION_BULK_FILE_MAX_SIZE,
  EXHIBITION_BULK_FILE_MAX_SIZE_TEXT,
  EXHIBITION_BULK_MAX_ROWS,
} from '../../../shared/constants/limits';
import { sizeCheckBig } from '../../../shared/util/fileSizeCheck';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import createIcon from '../../../asset/icon/create.png';
import downloadIcon from '../../../asset/icon/download.png';
import listIcon from '../../../asset/icon/list.png';
import EmptyList from '../../share/component/EmptyList';
import { useAdminExhibition } from '../../share/hook/useAdminExhibition';
import { useAdminExhibitionListSelection } from '../../share/hook/useAdminExhibitionListSelection';
import { useAdminListMarquee } from '../../share/hook/useAdminListMarquee';
import { useAdminExhibitionStore } from '../../share/store/adminExhibitionStore';
import { emptyExhibition } from './exhibitionDraft';
import ExhibitionInfo from './ExhibitionInfo';
import ExhibitionViewer from './ExhibitionViewer';
import ExhibitionViewerShell from './ExhibitionViewerShell';
import ExhibitionBulkPreview from './ExhibitionBulkPreview';
import ExhibitionBulkResult from './ExhibitionBulkResult';
import MultiExhibitionViewer from './MultiExhibitionViewer';
import { downloadExhibitionBulkTemplate } from './buildExhibitionBulkTemplate';
import {
  ExhibitionBulkSheetParseError,
  ExhibitionBulkSheetParseResult,
  parseExhibitionBulkSheet,
} from './parseExhibitionBulkSheet';
import { runAfterUnsavedExhibitionCheck } from './runAfterUnsavedExhibitionCheck';

const ExhibitionList: React.FC = () => {
  const { user } = useAuth();
  const authToken = useAuthTokenStore((state) => state.authToken);
  const {
    exhibitionList,
    activeExhibition,
    multiSelectedExhibitionIds,
    bulkResult,
    setActiveExhibition,
    setBulkResult,
    clearExhibitionSelection,
    selectExhibition,
    selectExhibitions,
    createBulk,
    load,
  } = useAdminExhibition();
  const pageSize = useAdminExhibitionStore((state) => state.pageSize);
  const enqueueAlert = useStandardAlertStore((state) => state.enqueueAlert);
  const listRef = useRef<HTMLElement>(null);
  const listCompRef = useRef<HTMLElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadRef = useRef(load);
  loadRef.current = load;
  const [bulkPreview, setBulkPreviewState] = useState<{
    fileName: string;
    result: ExhibitionBulkSheetParseResult;
  } | null>(null);

  const setBulkPreview = (
    preview: { fileName: string; result: ExhibitionBulkSheetParseResult } | null,
  ) => {
    setBulkPreviewState(preview);
    useAdminExhibitionStore.getState().setIsBulkPreviewOpen(preview !== null);
  };

  const selectionEnabled = !bulkPreview;
  const {
    handleExhibitionClick,
    handleListBackgroundClick,
    handleKeyDown,
    commitMarqueeExhibitionIds,
  } = useAdminExhibitionListSelection({
    exhibitionList,
    selectExhibition,
    selectExhibitions,
    disabled: !selectionEnabled,
    onSelectionStart: () => setBulkPreview(null),
  });
  const {
    hitIds,
    handleListMouseDown,
    handleListClickCapture,
    marqueeBox,
  } = useAdminListMarquee({
    listCompRef,
    commitMarqueeIds: commitMarqueeExhibitionIds,
    enabled: selectionEnabled,
    rowAttribute: 'data-exhibition-id',
  });
  const multiSelectedIdSet = new Set(multiSelectedExhibitionIds);
  const marqueeHitIdSet = new Set(hitIds.map(Number));
  const hasMultiSelected = multiSelectedExhibitionIds.length > 1;

  const hasViewer = !!activeExhibition
    || hasMultiSelected
    || !!bulkPreview
    || !!bulkResult;

  useEffect(() => {
    listRef.current?.focus();
  }, []);

  useEffect(() => () => {
    useAdminExhibitionStore.getState().setIsBulkPreviewOpen(false);
  }, []);

  useEffect(() => {
    if (!user || !authToken) return;
    void loadRef.current(1, Number(pageSize));
  }, [user, authToken, pageSize]);

  const openCreate = () => {
    if (activeExhibition && !activeExhibition.id) return;
    runAfterUnsavedExhibitionCheck(() => {
      const draft = emptyExhibition();
      draft.artists = [{
        artistId: null,
        name: '',
        nameEn: '',
        instagramHandle: '',
        architraveHandle: '',
      }];
      setBulkPreview(null);
      setBulkResult(null);
      useAdminExhibitionStore.getState().setHasChanged(false);
      setActiveExhibition(draft);
    });
  };

  const handleBulkFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!sizeCheckBig(file, EXHIBITION_BULK_FILE_MAX_SIZE)) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('FILE_TOO_BIG_PREVIEW', {
          maxSize: EXHIBITION_BULK_FILE_MAX_SIZE_TEXT,
        }),
      });
      return;
    }

    try {
      const result = await parseExhibitionBulkSheet(file);
      if (result.totalCount === 0) {
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('MISSING_REQUIRED_FIELD'),
        });
        return;
      }
      if (result.totalCount > EXHIBITION_BULK_MAX_ROWS) {
        enqueueAlert({
          type: AlertType.ALERT,
          position: AlertPosition.TOP,
          content: getAlertMessage('BULK_IMPORT_MAX_EXCEEDED', {
            maxCount: EXHIBITION_BULK_MAX_ROWS,
          }),
        });
        return;
      }
      runAfterUnsavedExhibitionCheck(() => {
        clearExhibitionSelection();
        setBulkResult(null);
        setBulkPreview({ fileName: file.name, result });
      });
    } catch (error) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: error instanceof ExhibitionBulkSheetParseError
          ? error.message
          : getAlertMessage('INVALID_VALUE_REQUESTED'),
      });
    }
  };

  const confirmBulk = async () => {
    if (!bulkPreview) return;
    const submitted = await createBulk(
      { exhibitionList: bulkPreview.result.exhibitionList },
      bulkPreview.result.invalidResults,
    );
    if (submitted) setBulkPreview(null);
  };

  return (
    <ListContainer ref={listRef} tabIndex={0} onKeyDown={handleKeyDown}>
      <List
        ref={listCompRef}
        onMouseDown={handleListMouseDown}
        onClickCapture={handleListClickCapture}
        onClick={handleListBackgroundClick}
      >
        {exhibitionList.map((exhibition) => (
          <ExhibitionInfo
            key={exhibition.id}
            data={exhibition}
            isSelected={
              (
                !!activeExhibition
                && activeExhibition.id > 0
                && Number(exhibition.id) === activeExhibition.id
              )
              || multiSelectedIdSet.has(Number(exhibition.id))
            }
            isMarqueeHit={marqueeHitIdSet.has(Number(exhibition.id))}
            handleClick={(event) => handleExhibitionClick(exhibition, event)}
          />
        ))}
        {marqueeBox && (
          <MarqueeSelectionBox
            style={{
              left: marqueeBox.left,
              top: marqueeBox.top,
              width: marqueeBox.width,
              height: marqueeBox.height,
            }}
          />
        )}
        <BtnContainer data-suppress-marquee>
          <UniversalIconBtn
            aria-label="Create exhibition"
            size="standard"
            border={true}
            backgroundImg={createIcon}
            onClick={openCreate}
          />
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={handleBulkFileChange}
          />
          <UniversalIconBtn
            aria-label="Import exhibitions from Excel"
            size="standard"
            border={true}
            backgroundImg={listIcon}
            onClick={() => fileInputRef.current?.click()}
          />
          <UniversalIconBtn
            aria-label="Download exhibition import template"
            size="standard"
            border={true}
            backgroundImg={downloadIcon}
            onClick={() => {
              void downloadExhibitionBulkTemplate().catch(() => {
                enqueueAlert({
                  type: AlertType.ALERT,
                  position: AlertPosition.TOP,
                  content: getAlertMessage('INVALID_VALUE_REQUESTED'),
                });
              });
            }}
          />
        </BtnContainer>
        {exhibitionList.length === 0 && <EmptyList message="exhibitions" />}
        <ListFill />
      </List>
      {hasViewer && (
        <ExhibitionViewerShell>
          {bulkPreview ? (
            <ExhibitionBulkPreview
              fileName={bulkPreview.fileName}
              result={bulkPreview.result}
              onCancel={() => setBulkPreview(null)}
              onConfirm={() => { void confirmBulk(); }}
            />
          ) : hasMultiSelected ? (
            <MultiExhibitionViewer />
          ) : activeExhibition ? (
            <ExhibitionViewer />
          ) : bulkResult ? (
            <ExhibitionBulkResult />
          ) : null}
        </ExhibitionViewerShell>
      )}
    </ListContainer>
  );
};

const ListContainer = styled.article`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  outline: none;
`;

const List = styled.section`
  position: relative;
  flex: 1;
  width: auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-bottom: 10vh;
  border-bottom: 1px solid ${({ theme }) => theme.colors.color_Gray_04};
  overflow-y: auto;
  user-select: none;
  &::-webkit-scrollbar { display: none; }
`;

const MarqueeSelectionBox = styled.div`
  position: absolute;
  pointer-events: none;
  box-sizing: border-box;
  border: 0.5px solid ${({ theme }) => theme.colors.color_Gray_05};
  background: transparent;
  backdrop-filter: blur(1px);
  z-index: 3;
`;

const BtnContainer = styled.div`
  position: fixed;
  bottom: 8vh;
  left: 50%;
  transform: translateX(-50%);
  z-index: 4;
  display: flex;
  gap: 0.5vw;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ListFill = styled.div`
  flex: 1 1 auto;
  align-self: stretch;
  width: 100%;
  min-height: 0;
`;

export default ExhibitionList;
